import { v4 as uuidv4 } from "uuid";
import {
  CheckIcon,
  XIcon,
  RefreshCcwIcon,
  FilePlus2,
  LoaderCircle,
  CircleCheckBig,
  FileWarning,
  RefreshCcw,
  Clock,
  Zap,
  Building2,
} from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";
import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "../../../../context/UserContext";
import { CreateKnowledgeBaseRequest, FileStatus } from "../../../../types";
import { createKnowledgeBase, generateKnowledgeBasePresignedUploadUrl, getFile } from "../../../../service/apiService";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { addFileToKnowledgeBase } from "../../../../helpers/knowledgeBaseLocalStorage";
import { isPDFScanned } from "../../../../helpers/pdfValidation";
import { toast } from "sonner";
import DepartmentSelector from "../HatManagementComponents/DepartmentSelector";

interface CreateKnowledgeBaseProps {
  onKnowledgeBaseChange: () => void;
  /** Pre-fills the BrainBank name (used when the form is opened from elsewhere
      with a name the user already typed). */
  initialName?: string;
  /** Called once the BrainBank exists, with what the opener needs to use it. */
  onCreated?: (created: { index_id: string; name: string; index_name: string }) => void;
}

interface KnowledgeBase {
  index_id: string;
  name: string;
  index_name: string;
}

interface FileUploadStatus {
  file: File;
  progress: number;
  status: "uploading" | "uploaded" | "not_processed" | "processing" | "processed" | "failed";
  errorMessage?: string;
  fileId?: string;
  uploadStarted?: boolean;
  useAdvancedProcessing?: boolean;
}

// Processing options
type ProcessingMode = "standard" | "advanced";

interface ProcessingOption {
  id: ProcessingMode;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  processingTime: string;
  recommended?: boolean;
}

const processingOptions: ProcessingOption[] = [
  {
    id: "standard",
    name: "Standard Processing",
    description: "Quick text extraction without AI enhancement.",
    icon: Clock,
    processingTime: "Quick processing",
    recommended: true
  },
  {
    id: "advanced",
    name: "Advanced Processing (AI Enhanced)",
    description: "AI-powered extraction with table analysis, image recognition, and deep content understanding.",
    icon: Zap,
    processingTime: "Queued processing (takes longer)",
  },
];

// Status mapping for user-friendly display
const statusLabels = {
  uploading: "Uploading...",
  uploaded: "Uploaded",
  not_processed: "In Queue",
  processing: "Processing",
  processed: "Processed Successfully",
  failed: "Failed",
};

const CreateKnowledgeBase: React.FC<CreateKnowledgeBaseProps> = ({
  onKnowledgeBaseChange,
  initialName,
  onCreated,
}) => {
  const { userId } = useUser();
  const [newKnowledgeBase, setNewKnowledgeBase] =
    useState<CreateKnowledgeBaseRequest>({
      knowledgebase_name: initialName ?? "",
      index_name: "",
      description: "",
      summary: ""
    });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fileUploadStatuses, setFileUploadStatuses] = useState<FileUploadStatus[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [createdKnowledgeBase, setCreatedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [defaultProcessingMode, setDefaultProcessingMode] = useState<ProcessingMode>("standard");

  // Department handlers
const handleDepartmentSelected = (departmentName: string) => {
  console.log(`Department selected: "${departmentName}"`);
  const trimmedName = departmentName.trim();
  setNewKnowledgeBase(prev => ({
    ...prev,
    department: trimmedName || undefined 
  }));
};

const handleDepartmentRemoved = () => {
  console.log("Department removed");
  setNewKnowledgeBase(prev => ({
    ...prev,
    department: undefined 
  }));
};

  useEffect(() => {
    // Automatically generate index_name from knowledgebase_name and append 5-digit UUID
    const generateUUID = () => {
      return uuidv4().slice(0, 5);
    };

    setNewKnowledgeBase((prev) => {
      let cleanName = prev.knowledgebase_name
        .toLowerCase()
        .replace(/[^a-zA-Z\s-]/g, "") // Remove special characters except hyphens and spaces
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .replace(/-+/g, "-"); // Replace multiple hyphens with a single hyphen

      // Remove leading or trailing hyphens
      cleanName = cleanName.replace(/^-+|-+$/g, "");

      return {
        ...prev,
        index_name: cleanName ? `${cleanName}-${generateUUID()}` : "",
      };
    });
    validateForm();
  }, [newKnowledgeBase.knowledgebase_name, newKnowledgeBase.description]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewKnowledgeBase((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!newKnowledgeBase.knowledgebase_name) {
      setValidationError("BrainBank name is required.");
      return false;
    }
    if (!newKnowledgeBase.description) {
      setValidationError("Description is required.");
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  
    if (!validateForm()) return;
  
    setIsSubmitting(true);
    setSubmitSuccess(null);
  
    try {
      // Step 1: Create the knowledge base
      const createdKB = await createKnowledgeBase(newKnowledgeBase);
      console.log("Knowledge base created:", createdKB);
      
      setCreatedKnowledgeBase({
        index_id: createdKB.index_id,  
        name: newKnowledgeBase.knowledgebase_name,
        index_name: newKnowledgeBase.index_name
      });
      
      setSubmitSuccess(true);
      
      // Modified file upload logic
      if (fileUploadStatuses.length > 0) {
        const filesToProcess = fileUploadStatuses.filter(
          status => status.status === "uploading" && !status.uploadStarted
        );
        
        // Mark all files as started before processing
        setFileUploadStatuses(prev => 
          prev.map(status => ({
            ...status,
            uploadStarted: filesToProcess.some(f => f.file === status.file) 
              ? true 
              : status.uploadStarted
          }))
        );
        
        // Process files sequentially
        for (const fileStatus of filesToProcess) {
          await startFileUpload(fileStatus.file, createdKB.index_id);
        }
      }
        
      setNewKnowledgeBase({
        knowledgebase_name: "",
        index_name: "",
        description: "",
        summary: ""
      });
      
      // Notify parent component
      onKnowledgeBaseChange();
      onCreated?.({
        index_id: createdKB.index_id,
        name: newKnowledgeBase.knowledgebase_name,
        index_name: newKnowledgeBase.index_name,
      });
    } catch (error) {
      console.error("Error creating knowledge base:", error);
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // File Upload functionality
const onDrop = React.useCallback(async (acceptedFiles: File[]) => {
  const newFileStatuses: FileUploadStatus[] = [];
  
  for (const file of acceptedFiles) {
    const isDuplicate = fileUploadStatuses.some(
      (existingStatus) => existingStatus.file.name === file.name
    );
    
    if (!isDuplicate) {
      // Check if PDF is scanned
      if (file.type === "application/pdf") {
        const isScanned = await isPDFScanned(file);
        
        if (isScanned && defaultProcessingMode === "standard") {
          toast.error(
            `"${file.name}" appears to be a scanned PDF. Please use Advanced Processing mode for better results.`
          );
          // Skip adding this file to the upload queue
          continue;
        }
      }
      
      newFileStatuses.push({
        file,
        progress: 0,
        status: "uploading",
        uploadStarted: false,
        useAdvancedProcessing: defaultProcessingMode === "advanced"
      });
    } else {
      console.log(`Skipping duplicate file: ${file.name}`);
    }
  }
  
  if (newFileStatuses.length > 0) {
    setFileUploadStatuses((prev) => [...prev, ...newFileStatuses]);
  }
}, [fileUploadStatuses, defaultProcessingMode, setDefaultProcessingMode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    maxFiles: 20,
    disabled: fileUploadStatuses.length >= 20 || !newKnowledgeBase.index_name,
    onDrop,
  });

  // Effect to refresh file statuses when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      refreshFileStatuses();
    }
  }, [refreshKey]);

  async function startFileUpload(file: File, knowledgeBaseId: string) {
    // Add check to prevent duplicate uploads
  const existingStatus = fileUploadStatuses.find(
    status => 
      status.file === file && 
      status.uploadStarted && 
      status.status !== "failed"
  );
  
  if (existingStatus) {
    console.log("File already being processed:", file.name);
    return;
  }
    console.log("startFileUpload received KB ID:", knowledgeBaseId)
    try {
      // Update status to show we're starting the upload
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? { ...status, uploadStarted: true }
            : status
        )
      );

      await handleFileUpload(file, knowledgeBaseId);
    } catch (error) {
      console.error("Error starting file upload:", error);
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: "failed",
                errorMessage: "Failed to start upload. Please try again.",
              }
            : status
        )
      );
    }
  }

  async function handleFileUpload(file: File, knowledgeBaseId: string) {
    if (!knowledgeBaseId || !file) {
      console.error("Missing required parameters for file upload");
      return;
    }
    console.log("handleFileUpload received KB ID:", knowledgeBaseId);
    if (!newKnowledgeBase.index_name) return;

    // Get the processing mode for this specific file
    const fileStatus = fileUploadStatuses.find(status => status.file === file);
    const useAdvancedProcessing = fileStatus?.useAdvancedProcessing ?? false;


    try {
      // Step 2: Generate presigned URL using the real knowledge base ID
      const presignedUrlResponse = await generateKnowledgeBasePresignedUploadUrl(
        file.name,
        newKnowledgeBase.index_name,
        knowledgeBaseId,
        defaultProcessingMode
      );

      const { presigned_url, file_id } = presignedUrlResponse;

      // Step 3: Upload the file to the presigned URL
      await axios.put(presigned_url, file, {
        headers: {
          "Content-Type": file.type || "application/pdf",
          "x-amz-meta-created_by": userId,
          "x-amz-meta-updated_by": userId,
          "x-amz-meta-index_name": newKnowledgeBase.index_name,
          "x-amz-meta-file_id": file_id,
          "x-amz-meta-processing_mode": defaultProcessingMode
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setFileUploadStatuses((prevStatuses) =>
            prevStatuses.map((status) =>
              status.file === file
                ? { ...status, progress: percentCompleted }
                : status
            )
          );
        },
      });

      // Update status to show the file was successfully uploaded
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? { ...status, status: "uploaded", progress: 100, fileId: file_id }
            : status
        )
      );

      // After upload, fetch the initial status
      fetchFileStatus(file_id, file);

    } catch (error) {
      console.error("Error uploading file:", error);
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: "failed",
                errorMessage: "Failed to upload file. Please try again.",
              }
            : status
        )
      );
    }
  }

  const fetchFileStatus = async (fileId: string, file: File) => {
    try {
      const response = await getFile(fileId);
      addFileToKnowledgeBase(response)
      // Convert the enum value to a string literal type
      const fileStatus = response.status ? response.status.toString() as FileUploadStatus['status'] : "failed";

      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: fileStatus,
                errorMessage: fileStatus === "failed" ? "Processing failed" : undefined,
              }
            : status
        )
      );
    } catch (error) {
      console.error("Error fetching file status:", error);
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: "failed",
                errorMessage: "Failed to fetch file status.",
              }
            : status
        )
      );
    }
  };

  const refreshFileStatuses = async () => {
    setIsRefreshing(true);
    try {
      const promises = fileUploadStatuses
        .filter(status => status.fileId && status.status !== "uploading")
        .map(status => fetchFileStatus(status.fileId!, status.file));
      
      await Promise.all(promises);
    } catch (error) {
      console.error("Error refreshing file statuses:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRetry = (file: File) => {
    if (!createdKnowledgeBase) {
      // Cannot retry without a knowledge base
      return;
    }
    
    setFileUploadStatuses((prevStatuses) =>
      prevStatuses.map((status) =>
        status.file === file
          ? {
              ...status,
              status: "uploading",
              progress: 0,
              errorMessage: undefined,
              uploadStarted: false
            }
          : status
      )
    );
  };

  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleClearAll = () => {
    setFileUploadStatuses([]);
  };

  // Get all files that have been uploaded (either completed or in process)
  const uploadedFiles = fileUploadStatuses.filter(
    status => status.status !== "uploading" && status.fileId
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "processing":
        return <LoaderCircle className="h-4 w-4 text-yellow-500" />;
      case "processed":
        return <CircleCheckBig className="h-4 w-4 text-green-600" />;
      case "not_processed":
        return <LoaderCircle className="h-4 w-4 text-gray-400" />;
      case "failed":
        return <FileWarning className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="py-2">
      <div className="mt-2 text-sm mx-2 p-4 rounded-lg bg-white">
        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div className="flex flex-col">
            <label
              htmlFor="knowledgebase_name"
              className="block text-sm font-medium text-gray-700"
            >
              BrainBank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="knowledgebase_name"
              name="knowledgebase_name"
              value={newKnowledgeBase.knowledgebase_name}
              onChange={handleInputChange}
              className="mt-2 w-full h-12 px-4 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg transition-all duration-200  focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:focus:border-violet-500 dark:placeholder-gray-400 focus:ring-opacity-20"
              required
            />
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="index_name"
              className="block text-sm font-medium text-gray-700"
            >
              Index Name (Auto-Generated based on above BrainBank name)
            </label>
            <input
              type="text"
              id="index_name"
              name="index_name"
              value={newKnowledgeBase.index_name}
              onChange={handleInputChange}
              className="mt-2 w-full h-12 px-4 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg transition-all duration-200 focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:focus:border-violet-500 dark:placeholder-gray-400 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:bg-gray-200 dark:disabled:bg-gray-800"
              required
              disabled
            />
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <TextareaAutosize
              id="description"
              name="description"
              value={newKnowledgeBase.description}
              onChange={handleInputChange}
              minRows={3}
              className="mt-2 w-full px-4 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg transition-all duration-200  focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:focus:border-violet-500 dark:placeholder-gray-400 focus:ring-opacity-20"
              required
            />
          </div>

          {/* Department Section */}
<div className="flex flex-col mb-6">
  <label
    htmlFor="department"
    className="block text-sm font-medium text-gray-700 mb-2"
  >
    Department
  </label>
  <div className="border border-gray-200 rounded-md p-4 h-48 overflow-y-auto">
    <DepartmentSelector
      selectedDepartmentName={newKnowledgeBase.department || ""}
      onDepartmentSelected={handleDepartmentSelected}
      onDepartmentRemoved={handleDepartmentRemoved}
      isEnabled={true}
    />
  </div>
  <p className="text-xs text-gray-500 mt-1">
    You can assign this BrainBank to a department or leave it unassigned.
  </p>
</div>

          {/* Processing Mode Selection */}
<div className="mt-6">
  <label className="block text-sm font-medium text-gray-700 mb-3">
    Default Processing Mode for Files
  </label>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {processingOptions.map((option) => {
      const IconComponent = option.icon;
      const isSelected = defaultProcessingMode === option.id;
      
      return (
        <div
          key={option.id}
          className={`relative cursor-pointer rounded-lg border p-4 transition-all ${
            isSelected
              ? "border-violet-500 bg-violet-50 ring-0 ring-violet-500"
              : "border-gray-200 bg-white hover:border-gray-300"
          }`}
          onClick={() => setDefaultProcessingMode(option.id)}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <IconComponent
                className={`h-6 w-6 mt-1 ${
                  isSelected ? "text-gray-600" : "text-gray-400"
                }`}
              />
            </div>
            <div className="ml-3 flex-1 overflow-hidden">
              <div className="flex items-center flex-wrap gap-1">
                <h3
                  className={`text-sm font-medium ${
                    isSelected ? "text-gray-900" : "text-gray-900"
                  }`}
                >
                  {option.name}
                </h3>
                {option.recommended && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Recommended
                  </span>
                )}
              </div>
              <p
                className={`mt-1 text-xs break-words ${
                  isSelected ? "text-gray-700" : "text-gray-500"
                }`}
              >
                {option.description}
              </p>
              <p
                className={`mt-2 text-xs font-medium ${
                  isSelected ? "text-gray-600" : "text-gray-400"
                }`}
              >
                Processing time: {option.processingTime}
              </p>
            </div>
            <input
              type="radio"
              name="processing-mode"
              value={option.id}
              checked={isSelected}
              onChange={() => setDefaultProcessingMode(option.id)}
              className="h-4 w-4 text-violet-600 border-gray-300 focus:ring-violet-500"
            />
          </div>
        </div>
      );
    })}
  </div>
</div>
          {/* Upload Files Section */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Files to BrainBank
            </label>
            
            {/* Upload area */}
            <div
              {...getRootProps({
                className: `dropzone flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
                  isDragActive
                    ? "border-violet-500"
                    : newKnowledgeBase.index_name
                    ? "border-gray-300 cursor-pointer"
                    : "border-gray-200 bg-gray-100 cursor-not-allowed"
                }`,
              })}
              style={{
                pointerEvents: newKnowledgeBase.index_name ? "auto" : "none",
              }}
            >
              <input
                {...getInputProps()}
                disabled={!newKnowledgeBase.index_name}
                accept=".pdf,.xlsx,.docx"
              />
              <div className="space-y-1 text-center">
                <FilePlus2 className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className={`cursor-pointer ${
                      newKnowledgeBase.index_name
                        ? "text-violet-600 hover:text-violet-500"
                        : "text-gray-400"
                    }`}
                  >
                    <span>Upload files</span>
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <ul className="list-disc list-inside text-xs text-gray-500 text-left">
                  <li>Maximum 20 files at a time</li>
                  <li>Accepted files: PDF, DOCX and XLSX</li>
                </ul>
              </div>
            </div>

            {/* Files currently uploading */}
            {/* <div className="mt-4">
              {fileUploadStatuses
                .filter(status => status.status === "uploading")
                .map((fileStatus, index) => (
                  <div key={`uploading-${index}`} className="mt-2 text-xs text-gray-500">
                    {fileStatus.file.name} - Uploading... {fileStatus.progress}%
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                      <div
                        className="bg-violet-600 h-2.5 rounded-full"
                        style={{ width: `${fileStatus.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
            </div> */}

            {/* Files Status Table */}
            {uploadedFiles.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-700">File Processing Status</h3>
                  <div className="flex space-x-2">
                    <button
                      type="button" // Important: add type="button" to prevent form submission
                      onClick={handleClearAll}
                      className="pr-5 inline-flex items-center text-xs text-red-600 hover:text-red-500"
                    >
                      Clear All
                    </button>
                    <button
                      type="button" // Important: add type="button" to prevent form submission
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      className="inline-flex items-center text-xs text-violet-600 hover:text-violet-500 disabled:text-gray-400"
                    >
                      <RefreshCcw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                      Refresh Status
                    </button>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-md mb-3 text-xs text-blue-800 flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2">
                    <p>File synchronization with BrainBank happens in the background and will continue even if you close this panel. Please check back later to see the final processing status.</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          File Name
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {uploadedFiles.map((fileStatus, index) => (
                        <tr key={`status-${index}`}>
                          <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-700 truncate max-w-xs">
                            {fileStatus.file.name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-xs">
                            <div className="flex items-center">
                              {getStatusIcon(fileStatus.status)}
                              <span className="ml-2">
                                {statusLabels[fileStatus.status]}
                              </span>
                            </div>
                            {fileStatus.errorMessage && (
                              <p className="text-red-500 text-xs mt-1">{fileStatus.errorMessage}</p>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-xs">
                            {fileStatus.status === "failed" && (
                              <button 
                                type="button"
                                onClick={() => handleRetry(fileStatus.file)}
                                disabled={!createdKnowledgeBase}
                                className="text-violet-600 hover:text-violet-500 disabled:text-gray-400"
                              >
                                Retry
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center">
            <button
              type="submit"
              className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                isSubmitting || validationError !== null
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
              disabled={isSubmitting || validationError !== null}
            >
              {isSubmitting ? (
                <>
                  <RefreshCcwIcon
                    className="animate-spin mr-2 h-5 w-5 text-white"
                    aria-hidden="true"
                  />
                  Creating...
                </>
              ) : (
                "Create BrainBank"
              )}
            </button>
            {submitSuccess !== null && (
              <div className="mt-2 sm:mt-0 sm:ml-2 flex items-center">
                {submitSuccess ? (
                  <>
                    <CheckIcon className="h-6 w-6 text-green-500" />
                    <span className="ml-2 text-sm text-green-500">
                      BrainBank successfully created
                    </span>
                  </>
                ) : (
                  <>
                    <XIcon className="h-6 w-6 text-red-500" />
                    <span className="ml-2 text-sm text-red-500">
                      Failed to create BrainBank
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateKnowledgeBase;